// components/Dropdown.js

import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "../css/components/dropdown.module.css";

export default function Dropdown({ label, name, options, value, onChange, placeholder = "Select", className, style, maxVisibleOptions, alignLeft = false, disabled = false }) {

  // Normalize and filter out any sentinel/placeholder-like options so "Select" is never an actual option
  const normalized = Array.isArray(options) ? options.map(String) : [];
  const ph = String(placeholder || "").trim().toLowerCase();
  const cleanedOptions = normalized.filter((opt) => {
    const o = String(opt).trim().toLowerCase();
    // remove common placeholder entries
    if (o === "select" || o === "select options" || (ph && o === ph)) return false;
    return true;
  });

  const handleChange = (eOrValue) => {
    if (typeof onChange !== "function") return;
    if (eOrValue && eOrValue.target) {
      onChange(eOrValue.target.value);
    } else {
      onChange(eOrValue);
    }
  };

  // When maxVisibleOptions is provided, render a custom non-searchable dropdown
  // with an internal scrollable options panel limited to that many visible rows.
  const useScrollableMenu = Number.isFinite(maxVisibleOptions) && maxVisibleOptions > 0;

  const [open, setOpen] = useState(false);

  if (useScrollableMenu) {
    const displayLabel = value || placeholder || "";
    const buttonStyle = alignLeft ? { textAlign: "left" } : undefined;

    const handleSelect = (opt) => {
      setOpen(false);
      handleChange(opt);
    };

    const handleBlur = () => {
      // Delay closing so click on option can register via onMouseDown
      setTimeout(() => setOpen(false), 100);
    };

    const maxHeightStyle = {
      maxHeight: `calc(var(--control-height, 40px) * ${maxVisibleOptions})`,
    };

    return (
      <div className={styles.dropdownContainer} style={style}>
        {label && (
          <label htmlFor={name} className={styles.dropdownLabel}>
            {label}
          </label>
        )}
        <div style={{ position: "relative" }}>
          <button
            type="button"
            className={`${styles.dropdown} ${className || ""}`}
            onClick={() => {
              if (disabled) return;
              setOpen((prev) => !prev);
            }}
            onBlur={handleBlur}
            style={buttonStyle}
            disabled={disabled}
          >
            {displayLabel}
          </button>
          {open && cleanedOptions.length > 0 && (
            <div className={styles.dropdownMenuInternal} style={maxHeightStyle}>
              {cleanedOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={styles.dropdownOptionInternal}
                  onMouseDown={(e) => {
                    if (disabled) return;
                    e.preventDefault();
                    handleSelect(option);
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleSelectChange = (e) => {
    handleChange(e.target.value);
  };

  return (
    <div className={styles.dropdownContainer}>
      {label && (
        <label htmlFor={name} className={styles.dropdownLabel}>
          {label}
        </label>
      )}
      <select
        id={name}
        name={name}
        className={`${styles.dropdown} ${className || ""}`}
        style={{
          ...style,
          ...(alignLeft ? { textAlign: "left", textAlignLast: "left" } : {}),
        }}
        value={value}
        onChange={handleSelectChange}
        disabled={disabled}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {cleanedOptions.map((option, idx) => (
          <option key={idx} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

Dropdown.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  maxVisibleOptions: PropTypes.number,
  alignLeft: PropTypes.bool,
  disabled: PropTypes.bool,
};